import { CONFIGURATION_APIS } from "@/api_framework/api_config";
import { IStaffProfile, IStaffClinicRow } from "@/api_framework/api_modals/Configuration";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useAPIUtils } from "@/app/hooks/useAPIUtils";
import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import axios, { AxiosError } from "axios";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import AxiosCurlirize from "axios-curlirize";

AxiosCurlirize(axios, () => {});

const REJECTION_LABELS: Record<string, string> = {
  NOT_FOUND: "no longer exists",
  INACTIVE: "is inactive",
  CITY_MISMATCH: "is not mapped to this clinic's city",
};

export const useStaffClinicMappingAPI = () => {
  const configuration = useConfiguration();
  const { captureError } = useAPIUtils();
  const { sendCurl } = useCurlLogger();
  const [clinics, setClinics] = useState<IStaffClinicRow[]>([]);
  const [cityStaff, setCityStaff] = useState<IStaffProfile[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const headers = () => ({
    Authorization: `${localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key)}`,
    "Content-Type": "application/json",
  });

  const onError = (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      captureError(axiosError);
      if (axiosError.config) {
        sendCurl(axiosError.config["curlCommand"]);
      }
    }
  };

  const fetchClinics = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(CONFIGURATION_APIS.STAFF_CLINIC_LIST.baseURL, {
        headers: headers(),
      });
      setClinics(response.data?.data ?? []);
    } catch (error) {
      setClinics([]);
      onError(error);
    } finally {
      setIsLoading(false);
    }
  }, [configuration]);

  const fetchCityStaff = useCallback(
    async (cityId: string) => {
      if (!cityId) {
        setCityStaff([]);
        return;
      }
      try {
        const response = await axios.get(`${CONFIGURATION_APIS.CITY_STAFF_LIST.baseURL}${cityId}/`, {
          headers: headers(),
        });
        setCityStaff(response.data?.data ?? []);
      } catch (error) {
        setCityStaff([]);
        onError(error);
      }
    },
    [configuration],
  );

  const saveMapping = useCallback(
    async (clinicId: string, staffIds: string[]) => {
      try {
        const response = await axios.post(
          `${CONFIGURATION_APIS.CLINIC_STAFF_MAP_UPDATE.baseURL}${clinicId}/`,
          { staff_ids: staffIds },
          { headers: headers() },
        );
        const rejected = response.data?.data?.rejected ?? [];
        if (rejected.length) {
          toast.error(
            `${rejected.length} staff could not be mapped: ${rejected
              .map((item: { reason: string }) => REJECTION_LABELS[item.reason] || item.reason)
              .join(", ")}`,
          );
        } else {
          toast.success("Clinic staff mapping updated");
        }
        return true;
      } catch (error) {
        toast.error("Could not update mapping");
        onError(error);
        return false;
      }
    },
    [configuration],
  );

  return { fetchClinics, fetchCityStaff, saveMapping, clinics, cityStaff, isLoading };
};
